import { useParams } from 'react-router-dom';

import { Box, Divider, Stack, Typography } from '@mui/material';

import {
  ABOUT_SECTIONS,
  type AboutBlock,
} from '@/designs-dev/about/About.content';

const Block = ({ block }: { block: AboutBlock }) => {
  if (block.type === 'h') {
    return (
      <Typography sx={{ mt: 2 }} variant='h5' color='primary.main'>
        {block.text}
      </Typography>
    );
  }
  if (block.type === 'ul') {
    return (
      <Stack sx={{ pl: 3, m: 0 }} component='ul' spacing={1}>
        {block.items.map((item) => (
          <Typography key={item} component='li' variant='body1'>
            {item}
          </Typography>
        ))}
      </Stack>
    );
  }
  if (block.type === 'code') {
    return (
      <Stack
        sx={{
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          px: 3,
          py: 2,
          m: 0,
          overflow: 'auto',
        }}
        component='pre'
      >
        {block.lines.map((line) => (
          <Typography
            sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
            key={line}
            component='code'
            variant='body2'
          >
            {line}
          </Typography>
        ))}
      </Stack>
    );
  }
  if (block.type === 'dl') {
    return (
      <Stack spacing={1}>
        {block.items.map((item) => (
          <Stack key={item.term} direction='row' spacing={2}>
            <Typography
              sx={{ fontFamily: 'monospace', width: 140, flexShrink: 0 }}
              variant='body2'
              color='text.primary'
            >
              {item.term}
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {item.desc}
            </Typography>
          </Stack>
        ))}
      </Stack>
    );
  }
  return (
    <Typography variant='body1' color='text.secondary'>
      {block.text}
    </Typography>
  );
};

// The About docs page: renders one section of the Loft documentation, chosen by
// the /about/:section route (defaults to the first). The docs nav lives in the
// sidebar (AboutMenu), swapped in by MenuLayout while you're in About.
const About = () => {
  const { section } = useParams();
  const current =
    ABOUT_SECTIONS.find((s) => s.slug === section) ?? ABOUT_SECTIONS[0];

  return (
    <Stack sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ mx: 'auto', maxWidth: 720, width: '100%', px: 4, py: 6 }}>
        <Typography variant='overline' color='text.secondary'>
          About Loft
        </Typography>
        <Typography sx={{ mt: 0.5 }} variant='h3' color='primary.main'>
          {current.title}
        </Typography>
        <Typography sx={{ mt: 1 }} variant='body1' color='text.secondary'>
          {current.blurb}
        </Typography>
        <Divider sx={{ my: 3 }} />
        <Stack spacing={2}>
          {current.blocks.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default About;
